import { codeMap, http, resource } from './http';

/**
 * 투자처 상세 7탭이 읽고 쓰는 표 — 공통 REST 그대로(`/api/<자원>`).
 * 정기보고 · 리스크평가는 **읽기만** 한다. 쓰는 흐름은 각 화면(정기보고 · 리스크 평가)에 있다.
 */
interface Row { id: number; companyId: number }

export interface Financial extends Row {
  fiscalYear: number; isForecast: boolean;
  /** FY 연간 · H1/H2 반기 · Q1~Q4 분기 (#77). 회계기간은 문서에 적힌 그대로 */
  fiscalPeriod: FinPeriod; periodStart: string | null; periodEnd: string | null;
  cashEquivalents: number | null; investmentAssets: number | null; totalAssets: number | null;
  currentLiabilities: number | null; noncurrentLiabilities: number | null; totalLiabilities: number | null;
  capitalStock: number | null; totalEquity: number | null;
  revenue: number | null; costOfSales: number | null; grossProfit: number | null; sgaExpenses: number | null;
  operatingIncome: number | null; interestExpense: number | null; netIncome: number | null;
  /** manual 수기 · ai 추출(검수 전) · ai_checked 검수 완료 */
  source: 'manual' | 'ai' | 'ai_checked'; documentId: number | null;
}
export interface Shareholder extends Row { asOf: string; name: string; shareKind: string; shares: number; vehicleId: number | null }
export interface Round extends Row {
  name: string; closedOn: string; preMoney: number | null; postMoney: number | null; amount: number;
  shares: number | null; pricePerShare: number | null; dealId: number | null;
}
export interface ShareChange extends Row { changedOn: string; kind: string; detail: string }
export interface History extends Row { occurredOn: string; text: string }
export interface GovProgram extends Row { name: string; startOn: string | null; endOn: string | null; amount: number | null }
export interface IpRight extends Row { kind: string; title: string; regNo: string | null; status: string }
export interface Loan extends Row { lender: string; amount: number; dueOn: string | null }
export interface Note extends Row { kind: string; occurredOn: string; authorName: string; body: string }

export interface ReportRequest { id: number; roundId: number; companyId: number; status: string; sentAt: string | null; submittedAt: string | null; confirmedAt: string | null }
export interface ReportRound { id: number; year: number; term: string; title: string }
export interface RiskAssessment { id: number; roundId: number; dealId: number; status: string; grade: string | null; opinion: string; submittedAt: string | null; approvedAt: string | null }
export interface RiskRound { id: number; code: string }

export const financials = resource<Financial>('financials');
export const shareholders = resource<Shareholder>('shareholders');
export const rounds = resource<Round>('rounds');
export const shareChanges = resource<ShareChange>('share-changes');
export const history = resource<History>('company-history');
export const govPrograms = resource<GovProgram>('gov-programs');
export const ipRights = resource<IpRight>('ip-rights');
export const loans = resource<Loan>('loans');
export const notes = resource<Note>('company-notes');

export const FIN_PERIODS = ['FY', 'H1', 'H2', 'Q1', 'Q2', 'Q3', 'Q4'] as const;
export type FinPeriod = (typeof FIN_PERIODS)[number];
export const FIN_PERIOD_LABEL: Record<FinPeriod, string> = { FY: '연간', H1: '상반기', H2: '하반기', Q1: '1분기', Q2: '2분기', Q3: '3분기', Q4: '4분기' };
export const FIN_SOURCE = codeMap({ manual: '수기', ai: 'AI · 검수 전', ai_checked: 'AI · 검수 완료' } as const);
export const IP_KIND = codeMap({ patent: '특허', utility: '실용신안', design: '디자인', trademark: '상표', copyright: '저작권', other: '기타' } as const);
export const IP_STATUS = codeMap({ filed: '출원', registered: '등록', expired: '소멸' } as const);
export const NOTE_KIND = codeMap({ review: '심사', meeting: '미팅', document: '자료', comment: '관리 코멘트', memo: '메모' } as const);
export const REQ_STATUS = codeMap({ not_sent: '미요청', requested: '요청', submitted: '제출', confirmed: '확정', rejected: '반려' } as const);
export const RISK_STATUS = codeMap({ not_started: '평가 전', draft: '작성 중', submitted: '제출', approved: '승인', rejected: '반려' } as const);

export const companyDetailApi = {
  /** 탭 전부 — 한 번에 */
  async load(companyId: number, dealIds: number[]) {
    const q = { companyId };
    const [fin, holders, rs, changes, hist, gov, ip, ln, ns, reqs, reportRounds, risks, riskRounds] = await Promise.all([
      financials.list(q), shareholders.list(q), rounds.list(q), shareChanges.list(q), history.list(q), govPrograms.list(q),
      ipRights.list(q), loans.list(q), notes.list(q),
      http.get<ReportRequest[]>('/report-requests', q), http.get<ReportRound[]>('/report-rounds'),
      dealIds.length ? http.get<RiskAssessment[]>('/risk-assessments', { dealId: dealIds.join(',') }) : Promise.resolve([] as RiskAssessment[]),
      http.get<RiskRound[]>('/risk-rounds'),
    ]);
    return { fin, holders, rounds: rs, changes, hist, gov, ip, loans: ln, notes: ns, reqs, reportRounds, risks, riskRounds };
  },
  /** 대시보드 · Raw 처럼 회사를 가로질러 볼 때 */
  all() {
    return Promise.all([financials.list(), shareholders.list(), rounds.list()]).then(([fin, holders, rs]) => ({ fin, holders, rounds: rs }));
  },
};
export type CompanyDetail = Awaited<ReturnType<typeof companyDetailApi.load>>;
